import { useState } from 'react';

import { FormChangeEventType, UseFormValidationResult, ValidationRules } from '@/hooks/common/useFormValidation/models';

export const useFormValidation = <T extends Record<string, any>>(
    initialValues: T,
    validationRules: ValidationRules<T>
): UseFormValidationResult<T> => {
    const [values, setValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

    // Handle input changes
    const handleChange = (e: FormChangeEventType) => {
        const { name, value } = e.target;

        // Assert `name` as a key of `T`
        const fieldName = name as keyof T;

        setValues((prevState) => ({
            ...prevState,
            [fieldName]: value
        }));

        // Clear the error for the field being updated
        if (fieldName in errors) {
            setErrors((prevState): Partial<Record<keyof T, string>> => {
                const remainingErrors = { ...prevState };
                delete remainingErrors[fieldName];
                return remainingErrors;
            });
        }
    };

    // Validate all fields or a single field
    const validate = (fieldToValidate?: keyof T): boolean => {
        const validationErrors: Partial<Record<keyof T, string>> = {};

        const fieldsToValidate = fieldToValidate ? ({ [fieldToValidate]: values[fieldToValidate] } as Partial<T>) : values;

        (Object.keys(fieldsToValidate) as Array<keyof T>).forEach((fieldName) => {
            if (validationRules[fieldName]) {
                const error = validationRules[fieldName]!(values[fieldName]);
                if (error) {
                    validationErrors[fieldName] = error;
                }
            }
        });

        setErrors((prevErrors) => ({
            ...prevErrors,
            ...validationErrors
        }));

        return Object.keys(validationErrors).length === 0;
    };

    // Handle form submission
    const onSubmit = (submitFallBack: (values: T) => void) => (e: any) => {
        e.preventDefault();

        if (validate()) {
            submitFallBack(values);
        }
    };

    return {
        values,
        errors,
        handleChange,
        onSubmit,
        validate
    };
};
